/**
 * Related markets row on a result card (Oct 2026): the other cards in this
 * result list for the same game (or the same player, on a prop card), each as
 * a chip with its short market name, side and best price. A tap takes the user
 * to that card (SgpResultsRenderer pages to it, utils/revealCard scrolls and
 * flashes it). Renders nothing when the list has no related cards.
 *
 * The list provides `RelatedMarketsContext` ({ keyOf, relatedFor, goTo });
 * a card outside a provider never shows the row.
 */
import React, { createContext, useState } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import PressableScale from './motion/PressableScale';
import { formatOdds } from '../utils/betCardFormatters';
import { uiVar } from './floodlight/uiStyleCss';

export const RelatedMarketsContext = createContext(null);

export default function RelatedMarketsRow({ items, onPress }) {
    const [frameW, setFrameW] = useState(0);
    const [trackW, setTrackW] = useState(0);
    if (!Array.isArray(items) || items.length === 0) return null;
    const overflow = frameW > 0 && trackW > frameW + 1;

    return (
        <View style={s.section}>
            <Text style={s.label} accessibilityRole="header">Related markets</Text>
            <View
                style={s.frame}
                onLayout={(e) => setFrameW(e.nativeEvent.layout.width)}
            >
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={s.scroller}
                    contentContainerStyle={s.track}
                    onContentSizeChange={(w) => setTrackW(w)}
                    accessibilityRole="list"
                    accessibilityLabel="Related markets"
                >
                    {items.map((item) => (
                        <PressableScale
                            key={item.key}
                            style={s.chip}
                            hoverStyle={s.chipHover}
                            pressedStyle={s.chipPressed}
                            scaleTo={0.96}
                            onPress={() => onPress && onPress(item.key)}
                            accessibilityRole="button"
                            accessibilityLabel={`${item.label}. Show card`}
                            hitSlop={{ top: 4, bottom: 4 }}
                        >
                            <Text style={s.short} numberOfLines={1}>{item.short}</Text>
                            {!!item.detail && <Text style={s.detail} numberOfLines={1}>{item.detail}</Text>}
                            <Text
                                style={[
                                    s.price,
                                    item.price > 0 && s.pricePos,
                                    item.price < 0 && s.priceNeg,
                                ]}
                            >
                                {formatOdds(item.price)}
                            </Text>
                            <Ionicons name="chevron-forward" size={12} color={uiVar('rs-text-faint', "rgba(255,255,255,0.35)")} />
                        </PressableScale>
                    ))}
                </ScrollView>
                {overflow && (
                    // Edge fade: says "more this way" without a scrollbar.
                    <LinearGradient
                        pointerEvents="none"
                        colors={['rgba(20,20,35,0)', 'rgba(20,20,35,0.96)']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={s.fade}
                    />
                )}
            </View>
        </View>
    );
}

const s = StyleSheet.create({
    section: {
        borderTopWidth: 1,
        borderTopColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.06)'),
        paddingTop: 10,
        paddingBottom: 12,
    },
    label: {
        paddingHorizontal: 16,
        marginBottom: 8,
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.45)'),
        fontSize: 10,
        fontWeight: '700',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
    },
    frame: {
        position: 'relative',
        width: '100%',
    },
    scroller: {
        flexGrow: 0,
        width: '100%',
    },
    track: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 16,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        minHeight: 36,
        paddingLeft: 12,
        paddingRight: 8,
        borderRadius: 999,
        backgroundColor: uiVar('rs-fill-wash2', 'rgba(255,255,255,0.05)'),
        borderWidth: 1,
        borderColor: uiVar('rs-line-hair', 'rgba(255,255,255,0.10)'),
    },
    chipHover: {
        backgroundColor: uiVar('rs-fill-wash3', 'rgba(255,255,255,0.09)'),
        borderColor: uiVar('rs-line-ink', 'rgba(162,89,255,0.45)'),
    },
    chipPressed: {
        backgroundColor: uiVar('rs-fill-select', 'rgba(162,89,255,0.16)'),
    },
    short: {
        color: uiVar('rs-text-ink', 'rgba(255,255,255,0.9)'),
        fontSize: 12,
        fontWeight: '700',
    },
    detail: {
        color: uiVar('rs-text-muted', 'rgba(255,255,255,0.5)'),
        fontSize: 12,
        maxWidth: 132,
    },
    price: {
        color: uiVar('rs-text-ink2', 'rgba(255,255,255,0.75)'),
        fontSize: 12,
        fontWeight: '700',
        ...(Platform.OS === 'web' ? { fontVariantNumeric: 'tabular-nums' } : { fontVariant: ['tabular-nums'] }),
    },
    pricePos: { color: uiVar('rs-text-pos', '#10B981') },
    priceNeg: { color: uiVar('rs-text-neg', '#EF4444') },
    fade: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: 32,
    },
});
